import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { tenantApi } from '../../services/api';
import TenantHeader from '../../components/msp/TenantHeader';
import './Msp.css';

const normalizeReports = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.reports)) return payload.reports;
  return [];
};

function TenantReports() {
  const { tenantId } = useParams();
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState('loading');
  const [generationStatus, setGenerationStatus] = useState('idle');
  const [scheduleFrequency, setScheduleFrequency] = useState('monthly');
  const [notificationPrefs, setNotificationPrefs] = useState({
    email: true,
    sms: false,
    slack: false,
  });
  const [settingsStatus, setSettingsStatus] = useState('idle');
  const [deliveryStatus, setDeliveryStatus] = useState({});
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    let isMounted = true;

    tenantApi
      .listReports(tenantId)
      .then((response) => {
        if (!isMounted) return;
        setReports(normalizeReports(response.data));
        const settings = response.data?.report_settings || response.data?.reportSettings;
        if (response.data?.comparison) {
          setComparison(response.data.comparison);
        }
        if (settings) {
          setScheduleFrequency(settings.frequency || settings.schedule || 'monthly');
          setNotificationPrefs({
            email: settings.notify_email ?? settings.email ?? true,
            sms: settings.notify_sms ?? settings.sms ?? false,
            slack: settings.notify_slack ?? settings.slack ?? false,
          });
        }
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  const handleSettingsSave = async (event) => {
    event.preventDefault();
    setSettingsStatus('submitting');
    try {
      await tenantApi.updateReportSettings(tenantId, {
        frequency: scheduleFrequency,
        notify_email: notificationPrefs.email,
        notify_sms: notificationPrefs.sms,
        notify_slack: notificationPrefs.slack,
      });
      setSettingsStatus('success');
    } catch (error) {
      console.error('Failed to update report settings', error);
      setSettingsStatus('error');
    }
  };

  const handleGenerateReport = async () => {
    setGenerationStatus('submitting');
    try {
      await tenantApi.generateReport(tenantId);
      setGenerationStatus('success');
      const response = await tenantApi.listReports(tenantId);
      setReports(normalizeReports(response.data));
    } catch (error) {
      console.error('Failed to generate report', error);
      setGenerationStatus('error');
    }
  };

  const handleDeliverReport = async (reportId, channel) => {
    setDeliveryStatus((prev) => ({ ...prev, [reportId]: 'submitting' }));
    try {
      await tenantApi.deliverReport(tenantId, reportId, { channel });
      setDeliveryStatus((prev) => ({ ...prev, [reportId]: 'success' }));
    } catch (error) {
      console.error('Failed to deliver report', error);
      setDeliveryStatus((prev) => ({ ...prev, [reportId]: 'error' }));
    }
  };

  const handleShareReport = async (reportId) => {
    setDeliveryStatus((prev) => ({ ...prev, [reportId]: 'submitting' }));
    try {
      await tenantApi.shareReport(tenantId, reportId);
      setDeliveryStatus((prev) => ({ ...prev, [reportId]: 'success' }));
    } catch (error) {
      console.error('Failed to share report', error);
      setDeliveryStatus((prev) => ({ ...prev, [reportId]: 'error' }));
    }
  };

  return (
    <div className="msp-page">
      <TenantHeader
        tenantId={tenantId}
        tenantName="Tenant Reports"
        subtitle="Monthly executive-ready summaries"
      >
        <button className="btn btn-primary" onClick={handleGenerateReport} disabled={generationStatus === 'submitting'}>
          {generationStatus === 'submitting' ? 'Generating…' : 'Generate Report'}
        </button>
      </TenantHeader>


      {generationStatus === 'error' && (
        <div className="msp-empty">Unable to generate report. Try again.</div>
      )}

      <div className="msp-section">
        <div className="msp-section-title">Report Scheduling</div>
        <form className="msp-form" onSubmit={handleSettingsSave}>
          <label className="msp-form-field">
            Frequency
            <select
              className="msp-input"
              value={scheduleFrequency}
              onChange={(event) => setScheduleFrequency(event.target.value)}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </label>
          <div className="msp-form-field">
            Notification Preferences
            <div className="msp-checkbox-group">
              <label className="msp-checkbox">
                <input
                  type="checkbox"
                  checked={notificationPrefs.email}
                  onChange={(event) =>
                    setNotificationPrefs({ ...notificationPrefs, email: event.target.checked })
                  }
                />
                Email
              </label>
              <label className="msp-checkbox">
                <input
                  type="checkbox"
                  checked={notificationPrefs.sms}
                  onChange={(event) =>
                    setNotificationPrefs({ ...notificationPrefs, sms: event.target.checked })
                  }
                />
                SMS
              </label>
              <label className="msp-checkbox">
                <input
                  type="checkbox"
                  checked={notificationPrefs.slack}
                  onChange={(event) =>
                    setNotificationPrefs({ ...notificationPrefs, slack: event.target.checked })
                  }
                />
                Slack
              </label>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={settingsStatus === 'submitting'}>
            {settingsStatus === 'submitting' ? 'Saving…' : 'Save Schedule'}
          </button>
        </form>
        {settingsStatus === 'error' && (
          <div className="msp-empty">Unable to save report settings.</div>
        )}
      </div>

      {status === 'loading' && <div className="msp-empty">Loading reports…</div>}
      {status === 'error' && <div className="msp-empty">Unable to load reports.</div>}
      {status === 'ready' && reports.length === 0 && (
        <div className="msp-empty">No reports available.</div>
      )}
      {status === 'ready' && reports.length > 0 && (
        <>
          <table className="msp-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Generated</th>
                <th>Download</th>
                <th>Delivery</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => {
                const reportId = report.id || report.report_id;
                const statusLabel = report.status || 'In Progress';
                const progressValue = report.progress || (statusLabel === 'Complete' ? 100 : 60);
                return (
                  <tr key={reportId}>
                    <td>{report.title || report.name || 'Report'}</td>
                    <td>{statusLabel}</td>
                    <td>
                      <div className="msp-progress msp-progress-inline">
                        <div className="msp-progress-bar" style={{ width: `${progressValue}%` }}></div>
                        <span className="msp-progress-text">{progressValue}%</span>
                      </div>
                    </td>
                    <td>{report.generated_at || report.created_at || 'Pending'}</td>
                    <td>
                      {report.download_url ? (
                        <a className="msp-link" href={report.download_url}>
                          Download PDF
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <div className="msp-action-row">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleDeliverReport(reportId, 'email')}
                        >
                          Email
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleShareReport(reportId)}
                        >
                          Share Link
                        </button>
                      </div>
                      {deliveryStatus[reportId] === 'error' && (
                        <div className="msp-inline-error">Delivery failed.</div>
                      )}
                    </td>
                    <td>
                      <Link
                        className="msp-link"
                        to={`/msp/tenants/${tenantId}/reports/${reportId}`}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {comparison && (
            <div className="msp-section">
              <div className="msp-section-title">Historical Comparison</div>
              <div className="msp-card">
                <span className="msp-card-label">Summary</span>
                <span className="msp-card-value">{comparison.summary || comparison.delta || 'No changes tracked.'}</span>
                {comparison.key_changes && (
                  <div className="msp-card-label">Key Changes: {comparison.key_changes}</div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TenantReports;
